/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { isRightToLeft } from 'js/lib/language';

import { Button, Grid, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import Section from 'bundles/front-page/components/Section';
import useLohpCollection from 'bundles/front-page/hooks/useLohpCollection';
import CompactLearningProductCard from 'bundles/front-page/redesign-modules/spotlight-collections/CompactLearningProductCard';
import SpotlightCollectionsModulePlaceholder from 'bundles/front-page/redesign-modules/spotlight-collections/SpotlightCollectionsModulePlaceholder';

import _t from 'i18n!nls/front-page';

const MAX_PRODUCT_CARDS = 4;

type Props = {
  headline: string;
  description: string;
  ctaText: string;
  ctaTarget: string;
  collectionSets: {
    id: string;
    collectionModuleId: string;
    presetCollectionId?: string;
    filterName?: string;
  }[];
  linearGradientBackground: {
    angle: string;
    colors: string[];
    stops: number[];
  };
};

const styles = {
  container: css`
    padding: var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-300);
    display: grid;
    grid-template-columns: 1fr 4fr;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('md')} {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      gap: var(--cds-spacing-200);
    }

    ${breakpoints.up('lg')} {
      padding-left: var(--cds-spacing-400);
    }
  `,
  containerWithBackground: css`
    margin: var(--cds-spacing-200) 0;
    padding: var(--cds-spacing-200) 0;
  `,
  sectionTitle: css`
    ${breakpoints.down('xs')} {
      ${typography2.subtitleMedium}
    }
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    ${breakpoints.down('md')} {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  `,
  description: css`
    margin-top: var(--cds-spacing-50);
  `,
  cta: css`
    margin-top: var(--cds-spacing-150);
  `,
  ctaIconButton: css`
    padding: 0;

    ${breakpoints.down('md')} {
      &:hover {
        box-shadow: 0 0 0 1px var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  collection: css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--cds-spacing-150);
    width: 100%;

    ${breakpoints.down('xs')} {
      /* 600 / 4 = 150 */
      grid-template-columns: repeat(4, minmax(150px, 1fr));
      width: auto;
    }

    /* !important to override the styles set by CDS */
    .cds-ProductCard-grid {
      min-width: unset !important;
      border-radius: var(--cds-border-radius-100) !important;
    }

    .cds-Avatar-base {
      width: 12px !important;
      height: 12px !important;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cds-ProductCard-footer {
      gap: 0;
    }

    ${breakpoints.down('xs')} {
      .cds-CommonCard-title {
        ${typography2.subtitleSmall}
      }

      .cds-CommonCard-metadata,
      .cds-CommonCard-metadata p,
      .cds-ProductCard-partnerNames {
        ${typography2.bodyTertiary}
      }

      .cds-ProductCard-gridPreviewContainer .cds-CommonCard-previewImage {
        margin: calc(var(--cds-spacing-100) * -1) calc(var(--cds-spacing-100) * -1) var(--cds-spacing-100)
          calc(var(--cds-spacing-100) * -1) !important;
        border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0 !important;
      }
    }
  `,
};

export const getLinearGradientBackground = (
  linearGradientBackground: Props['linearGradientBackground'],
  isRTL: boolean
) => {
  const colorStopList = isRTL
    ? [...linearGradientBackground?.colors]
        .reverse()
        .map((color, index) => `${color} ${linearGradientBackground?.stops[index]}%`)
    : linearGradientBackground?.colors.map((color, index) => `${color} ${linearGradientBackground?.stops[index]}%`);

  return `linear-gradient(${linearGradientBackground?.angle}, ${colorStopList})`;
};

const SpotlightCollectionsModuleRenderer = ({
  headline,
  description,
  ctaText,
  ctaTarget,
  collectionSets,
  linearGradientBackground,
}: Props) => {
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('md'));
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const currentCollectionSetIndex = 0;
  const collectionIndex = currentCollectionSetIndex;
  const isRTL = isRightToLeft(_t.getLocale());
  const track = useTracker();

  const { collection, loading: isCollectionsLoading } = useLohpCollection({
    queryCollectionId: collectionSets[currentCollectionSetIndex]!.collectionModuleId,
    additionalInput: {
      preset_collection_id: collectionSets[currentCollectionSetIndex]!.presetCollectionId,
    },
  });

  const moduleEventingData = {
    moduleType: 'collections_module' as MerchandisingModuleModuleType,
    heading: headline ?? '',
    body: description ?? '',
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: moduleEventingData,
    },
    [],
    { fullyVisible: true }
  );

  if (!isCollectionsLoading && !collection?.entities.length) {
    return null;
  }

  if (isCollectionsLoading) {
    return <SpotlightCollectionsModulePlaceholder numberOfCards={MAX_PRODUCT_CARDS} />;
  }

  const trackClick = () => {
    track('click_button', {
      button: {
        name: 'view_all_products',
        linkURL: ctaTarget ?? '',
        linkName: ctaText ?? '',
        linkType: 'internal',
      },
    });
  };

  const output = (
    <>
      <div css={styles.contentContainer}>
        <Typography2 variant="subtitleLarge" component="h2" color="invertBody" css={styles.sectionTitle}>
          {headline}
        </Typography2>
        {description && (
          <Typography2 variant="bodyPrimary" color="invertBody" component="p" css={styles.description}>
            {description}
          </Typography2>
        )}
        {ctaText &&
          (isTabletOrSmaller ? (
            <Button
              variant="ghostInvert"
              component="a"
              href={ctaTarget}
              css={styles.ctaIconButton}
              aria-label={ctaText}
              onClick={trackClick}
            >
              <ArrowNextIcon color="invert" size="large" />
            </Button>
          ) : (
            <Button
              variant="primaryInvert"
              size="small"
              component="a"
              href={ctaTarget}
              css={styles.cta}
              onClick={trackClick}
              iconPosition="after"
              icon={<ArrowNextIcon size="medium" />}
            >
              {ctaText}
            </Button>
          ))}
      </div>
      <OverflowCarousel css={styles.collection} shouldAllowSpaceForHoverItems>
        {collection?.entities
          .filter((entity) => !!entity.name)
          .slice(0, MAX_PRODUCT_CARDS)
          .map((entity, index) => (
            <CompactLearningProductCard
              entity={entity}
              entityIndex={index}
              collectionIndex={collectionIndex}
              collection={{
                ...collection,
                title: headline,
                collectionTrackingData: {
                  id: collectionSets[currentCollectionSetIndex]!.id,
                },
              }}
              key={entity.id}
            />
          ))}
      </OverflowCarousel>
    </>
  );

  const background = getLinearGradientBackground(linearGradientBackground, isRTL);

  if (isMobile) {
    return (
      <Section css={styles.containerWithBackground} style={{ background }} ref={visibilityTrackingRef}>
        {output}
      </Section>
    );
  }

  return (
    <Section ref={visibilityTrackingRef}>
      <Grid item xs={12} css={styles.container} style={{ background }}>
        {output}
      </Grid>
    </Section>
  );
};

export default SpotlightCollectionsModuleRenderer;
